import type { Model, ProfileSeed, ThroughputPoint } from '@/types';

const gib = (value: number) => value * 1024 ** 3;
const mib = (value: number) => value * 1024 ** 2;
const kib = (value: number) => value * 1024;

export const MODEL_FIXTURES: Model[] = [
  {
    model_id: 'llama3-8b',
    family: 'meta-llama3',
    display_name: 'Llama 3 8B',
    params_b: 8,
    max_seq_len: 8192,
    weights_mem_json: {
      4: gib(5.1),
      8: gib(10.2),
      16: gib(20.4),
    },
    kv_per_token_json: {
      4: kib(320),
      8: kib(512),
      16: kib(1024),
    },
    base_runtime_overhead_bytes: gib(4.2),
    meta: {
      provider: 'Meta',
      release: '2024-04',
    },
  },
  {
    model_id: 'llama3-70b',
    family: 'meta-llama3',
    display_name: 'Llama 3 70B',
    params_b: 70,
    max_seq_len: 8192,
    weights_mem_json: {
      4: gib(44.8),
      8: gib(89.6),
      16: gib(179.2),
    },
    kv_per_token_json: {
      4: kib(150),
      8: kib(192),
      16: kib(480),
    },
    base_runtime_overhead_bytes: gib(8.4),
    meta: {
      provider: 'Meta',
      release: '2024-04',
    },
  },
  {
    model_id: 'mistral-large-2411',
    family: 'mistral',
    display_name: 'Mistral Large (Nov 2024)',
    params_b: 123,
    max_seq_len: 131072,
    weights_mem_json: {
      4: gib(55.0),
      8: gib(110.0),
      16: gib(220.0),
    },
    kv_per_token_json: {
      4: mib(2.2),
      8: mib(3.4),
      16: mib(6.8),
    },
    base_runtime_overhead_bytes: gib(9.6),
    meta: {
      provider: 'Mistral AI',
      release: '2024-11',
    },
  },
  {
    model_id: 'phi-4-mini',
    family: 'phi',
    display_name: 'Phi-4 Mini',
    params_b: 14,
    max_seq_len: 4096,
    weights_mem_json: {
      4: gib(7.2),
      8: gib(14.4),
      16: gib(28.8),
    },
    kv_per_token_json: {
      4: kib(420),
      8: kib(640),
      16: kib(1280),
    },
    base_runtime_overhead_bytes: gib(5.0),
    meta: {
      provider: 'Microsoft',
      release: '2024-10',
    },
  },
];

export const THROUGHPUT_FIXTURES: ThroughputPoint[] = [
  // Meta Llama 3 8B
  {
    model_id: 'llama3-8b',
    backend: 'gaudi3_pcie',
    precision: 4,
    batch: 1,
    prompt_tps: 310,
    decode_tps: 250,
  },
  {
    model_id: 'llama3-8b',
    backend: 'gaudi3_pcie',
    precision: 4,
    batch: 4,
    prompt_tps: 590,
    decode_tps: 470,
  },
  {
    model_id: 'llama3-8b',
    backend: 'gaudi3_pcie',
    precision: 8,
    batch: 1,
    prompt_tps: 240,
    decode_tps: 185,
  },
  {
    model_id: 'llama3-8b',
    backend: 'gaudi3_pcie',
    precision: 8,
    batch: 4,
    prompt_tps: 455,
    decode_tps: 350,
  },
  {
    model_id: 'llama3-8b',
    backend: 'gaudi3_pcie',
    precision: 16,
    batch: 1,
    prompt_tps: 160,
    decode_tps: 120,
  },
  // Meta Llama 3 70B
  {
    model_id: 'llama3-70b',
    backend: 'gaudi3_pcie',
    precision: 4,
    batch: 1,
    prompt_tps: 72,
    decode_tps: 55,
  },
  {
    model_id: 'llama3-70b',
    backend: 'gaudi3_pcie',
    precision: 4,
    batch: 2,
    prompt_tps: 128,
    decode_tps: 92,
  },
  {
    model_id: 'llama3-70b',
    backend: 'gaudi3_pcie',
    precision: 8,
    batch: 1,
    prompt_tps: 58,
    decode_tps: 42,
  },
  {
    model_id: 'llama3-70b',
    backend: 'gaudi3_pcie',
    precision: 8,
    batch: 2,
    prompt_tps: 102,
    decode_tps: 74,
  },
  // Mistral Large 24.11
  {
    model_id: 'mistral-large-2411',
    backend: 'gaudi3_pcie',
    precision: 4,
    batch: 1,
    prompt_tps: 64,
    decode_tps: 48,
  },
  {
    model_id: 'mistral-large-2411',
    backend: 'gaudi3_pcie',
    precision: 8,
    batch: 1,
    prompt_tps: 49,
    decode_tps: 36,
  },
  {
    model_id: 'mistral-large-2411',
    backend: 'gaudi3_pcie',
    precision: 8,
    batch: 2,
    prompt_tps: 86,
    decode_tps: 63,
  },
  // Phi-4 Mini
  {
    model_id: 'phi-4-mini',
    backend: 'gaudi3_pcie',
    precision: 4,
    batch: 1,
    prompt_tps: 180,
    decode_tps: 140,
  },
  {
    model_id: 'phi-4-mini',
    backend: 'gaudi3_pcie',
    precision: 8,
    batch: 1,
    prompt_tps: 140,
    decode_tps: 108,
  },
  {
    model_id: 'phi-4-mini',
    backend: 'gaudi3_pcie',
    precision: 8,
    batch: 4,
    prompt_tps: 255,
    decode_tps: 196,
  },
];

export const PROFILE_FIXTURES: ProfileSeed[] = [
  {
    profile_id: 'chat-lite',
    display_name: 'Chat Lite',
    default_model_id: 'llama3-8b',
    default_precision: 8,
    default_input_tokens: 180,
    default_output_tokens: 120,
    default_latency_slo_ms: 1000,
    notes: 'Baseline conversational agent tuned for fast first response.',
  },
  {
    profile_id: 'chat-knowledge',
    display_name: 'Knowledge Assistant',
    default_model_id: 'llama3-70b',
    default_precision: 4,
    default_input_tokens: 600,
    default_output_tokens: 350,
    default_latency_slo_ms: 1800,
    notes: 'Higher fidelity answers with retrieval-augmented prompts.',
  },
  {
    profile_id: 'agent-planner',
    display_name: 'Agentic Planner',
    default_model_id: 'mistral-large-2411',
    default_precision: 8,
    default_input_tokens: 1200,
    default_output_tokens: 800,
    default_latency_slo_ms: 2500,
    notes: 'Long-horizon reasoning and orchestration workloads.',
  },
  {
    profile_id: 'assist-customer',
    display_name: 'Customer Assist Bot',
    default_model_id: 'phi-4-mini',
    default_precision: 8,
    default_input_tokens: 220,
    default_output_tokens: 140,
    default_latency_slo_ms: 900,
    notes: 'Support flows with moderate context and rapid follow ups.',
  },
];
